"use client";

import { useCallback, useSyncExternalStore } from "react";
import { useStudio } from "@/contexts/studio-context";

const NO_STUDIO_UNSUBSCRIBE = () => {};

/**
 * Returns the current 1-based measure number.
 *
 * Pushed by the studio, not polled. The measure can only change while the
 * render loop is running — playback scrolls the notes, a navigation gesture
 * moves the view offset — and the loop is itself parked at idle by the
 * render-on-demand governor. Subscribing therefore costs nothing when
 * nothing moves, where the previous 10 fps `setInterval` kept waking the
 * main thread forever to observe that nothing had happened.
 *
 * Covers playback and scrolling alike: both go through `getEffectiveTick()`.
 */
export function useCurrentMeasure(): number {
  const { studio } = useStudio();

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (!studio) {
        return NO_STUDIO_UNSUBSCRIBE;
      }

      return studio.onMeasureChange(onStoreChange);
    },
    [studio],
  );

  // `getCurrentMeasure` est lisible avant la première frame, ce qui couvre le
  // montage : la vue est créée en asynchrone et peut avoir déjà publié.
  const getSnapshot = useCallback(() => studio?.getCurrentMeasure() ?? 1, [studio]);

  return useSyncExternalStore(subscribe, getSnapshot, () => 1);
}
